import {HttpClient, HttpEvent, HttpEventType, HttpParams} from '@angular/common/http';
import {BaseService} from '@shared/services/api/base-service';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AlertTicket, AlertTicketImpl, AlertTicketSearchParams, AttachmentUploadUrl, BulkTicketAggregationParams, EscalatedTicket, EscalatedTicketBody, EscalatedTicketCreateBody, EscalatedTicketImpl, EscalatedTicketSearchParams, EscalatedTicketUpdateBody, PartialTicket, SiteService, Ticket, TicketAggregationParams, TicketAPIResultFull, TicketAPIResultPartial, TicketAttachment, TicketBody, TicketChangeHistory, TicketComment, TicketCommentAPIResponse, TicketCommentCreationFields, TicketCommentEditFields, TicketCommentImpl, TicketCommentResult, TicketEmail, TicketSearchParams, TicketType} from '@shared/services';
import {Observable, of} from 'rxjs';
import {last, map, mergeMap} from 'rxjs/operators';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {UserService} from '@app/layout/admin/services/users/user.service';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {ClassTransformer} from 'class-transformer';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import Fields from 'gql-query-builder/build/Fields';
import VariableOptions from 'gql-query-builder/build/VariableOptions';
import {DefaultFieldAggregates, FieldAggregates, PagedModel} from '@shared/api/model/paged-model';
import * as gql from 'gql-query-builder';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import IQueryBuilderOptions from 'gql-query-builder/build/IQueryBuilderOptions';
import {TicketsConstants} from '@shared/services/api/tickets/tickets-constants';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {MomentUtil} from '@shared/util/datetime/moment-util';
import {isNestedField} from "gql-query-builder/build/NestedField";

export interface TicketFetchOptions {
  includeLinkedTickets?: boolean;
}

export interface AlertTicketFetchOptions extends TicketFetchOptions {
  includeTimeInFault?: boolean;
}

export interface EscalatedTicketFetchOptions extends TicketFetchOptions {
  includeTicketAge?: boolean;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

interface SiteAssignees {
  site_id: number;
  assignees: User[];
}



const TICKET_FIELD_TYPE = 'type';
const USER_WITH_ENTITY_FIELDS = ['id', 'firstname', 'lastname', 'email', { entity: ['id', 'name', 'type', 'logo_url'] }];
const BASE_TICKET_COMMENT_FIELDS = ['comment_id', 'text', 'created_at', 'author_full_name', 'author_id'];
const GRAPHQL_TYPE_TICKET_TYPE = 'TicketType';
const GRAPHQL_TYPE_TICKET_TOTALS_FIELD = 'TicketTotalsField';


const TICKET_COMMENTS_QUERY_VARIABLES = {
  user_only: { type: GRAPHQL_TYPES.BOOLEAN, value: true },
  commentsFirst: { name: 'first', type: GRAPHQL_TYPES.INTEGER, },
  commentsOffset: { name: 'offset', type: GRAPHQL_TYPES.INTEGER, }
};

const TICKET_COMMENTS_QUERY: IQueryBuilderOptions = {
  operation: 'comments',
  fields: BASE_TICKET_COMMENT_FIELDS,
  variables: TICKET_COMMENTS_QUERY_VARIABLES
};

const ESCALATED_TICKET_COMMENTS_QUERY: IQueryBuilderOptions = {
  operation: TICKET_COMMENTS_QUERY.operation,
  fields: [
    ...TICKET_COMMENTS_QUERY.fields,
    {
      author: USER_WITH_ENTITY_FIELDS
    }
  ],
  variables: TICKET_COMMENTS_QUERY.variables
};

const BASE_TICKET_FIELDS = [
  'ticket_id', 'site_id', TICKET_FIELD_TYPE, 'summary', 'solution', 'description', 'savings', 'manual_savings',
  'created_at', 'resolved_at',
  'ticket_archived', 'updated_at',
  { status: ['status_id', 'status_name', 'display_text'] },
  { priority: ['priority_id', 'name'] }
];

const ATTACHMENT_LINK_FIELD_DEFINITION = { link: ['expiration_ts', 'url'] };
const TICKET_ATTACHMENT_FIELDS = [
  'attachment_id', 'file_name', 'mime_type', 'author_id',
  ATTACHMENT_LINK_FIELD_DEFINITION,
  { preview: ['height', 'width', ATTACHMENT_LINK_FIELD_DEFINITION] }
];

const ESCALATED_TICKET_FIELDS = [
  ...BASE_TICKET_FIELDS,
  'inbox', 'equipment_ids', 'time_taken',
  { benefit: ['benefit_id', 'benefit_name', 'emoji'] },
  { assignees: USER_WITH_ENTITY_FIELDS },
  { attachments: TICKET_ATTACHMENT_FIELDS },
  'comment_count',
  ESCALATED_TICKET_COMMENTS_QUERY
];

const TICKET_AGE_FIELD = 'age';

const ALERT_TICKET_FIELDS = [
  ...BASE_TICKET_FIELDS, 'impact_ids', 'rule_id', 'inbox', 'equipment_ids', 'fault_status', 'last_fault_transitioned_at', 'power_saving'
];

const TIME_IN_FAULT_FIELD = 'time_in_fault';

const TICKETS_PAGINATION_SEARCH_VARIABLES = {
  first: { type: GRAPHQL_TYPES.INTEGER },
  offset: { type: GRAPHQL_TYPES.INTEGER },
};

const BASE_TICKETS_SEARCH_VARIABLES = {
  ...TICKETS_PAGINATION_SEARCH_VARIABLES,
  ticket_id: { type: GRAPHQL_TYPES.STRING },
  ticket_ids: { type: GRAPHQL_TYPES.STRING, list: true },
  site_id: { type: GRAPHQL_TYPES.INTEGER },
  site_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
  type: { type: GRAPHQL_TYPE_TICKET_TYPE },
  types: { type: GRAPHQL_TYPE_TICKET_TYPE, list: true },
  inbox: { type: GRAPHQL_TYPES.BOOLEAN },
  has_ticket_links: { type: GRAPHQL_TYPES.BOOLEAN },
  linked_ticket_recorded_at_local_end: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  created_at_start: { type: GRAPHQL_TYPES.DATE_TIME },
  created_at_end: { type: GRAPHQL_TYPES.DATE_TIME },
  created_at_local_start: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  created_at_local_end: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  resolved_at_start: { type: GRAPHQL_TYPES.DATE_TIME },
  resolved_at_end: { type: GRAPHQL_TYPES.DATE_TIME },
  resolved_at_local_start: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  resolved_at_local_end: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  resolved: {type: GRAPHQL_TYPES.BOOLEAN},
  updated_at_start: { type: GRAPHQL_TYPES.DATE_TIME },
  updated_at_end: { type: GRAPHQL_TYPES.DATE_TIME },
  updated_at_local_start: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  updated_at_local_end: { type: GRAPHQL_TYPES.LOCAL_DATE_TIME },
  status_id: { type: GRAPHQL_TYPES.INTEGER },
  status_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
  orderBy: { type: GRAPHQL_TYPES.STRING },
  summary_web_search: { type: GRAPHQL_TYPES.STRING },
  ticket_archived: { type: GRAPHQL_TYPES.BOOLEAN },
  rule_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
  metadata_type_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
  equipment_ids: {type: GRAPHQL_TYPES.LONG, list: true},
  level_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  zone_name_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  priority_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  has_savings: { type: GRAPHQL_TYPES.BOOLEAN },
  assignee_ids: { type: GRAPHQL_TYPES.STRING, list: true },
  totals: { type: GRAPHQL_TYPE_TICKET_TOTALS_FIELD, list: true }
};

const ALERT_TICKETS_SEARCH_VARIABLES = {
  ...BASE_TICKETS_SEARCH_VARIABLES,
  in_alert_local_ts_range: { type: GRAPHQL_TYPES.LOCAL_INTERVAL },
  template_ids: {type: GRAPHQL_TYPES.UUID, list: true},
  template_category_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  impact_ids: {type: GRAPHQL_TYPES.UUID, list: true},
  fault_statuses: {type: GRAPHQL_TYPES.FAULT_STATUS, list: true},
  rule_state: {type: GRAPHQL_TYPES.RULE_STATE},
  rule_states: {type: GRAPHQL_TYPES.RULE_STATE, list: true},
  time_in_fault_gt: {type: GRAPHQL_TYPES.LONG},
  time_in_fault_gt_eq: {type: GRAPHQL_TYPES.LONG},
  time_in_fault_lt: {type: GRAPHQL_TYPES.LONG},
  time_in_fault_lt_eq: {type: GRAPHQL_TYPES.LONG}
};

const ESCALATED_TICKETS_SEARCH_VARIABLES = {
  ...BASE_TICKETS_SEARCH_VARIABLES,
  benefit_id: { type: GRAPHQL_TYPES.INTEGER },
  benefit_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
  comment_author_ids: { type: GRAPHQL_TYPES.STRING, list: true },
  has_comments: { type: GRAPHQL_TYPES.BOOLEAN },
  age_gt: {type: GRAPHQL_TYPES.LONG},
  age_gt_eq: {type: GRAPHQL_TYPES.LONG},
  age_lt: {type: GRAPHQL_TYPES.LONG},
  age_lt_eq: {type: GRAPHQL_TYPES.LONG},
  age_cutoff_local_ts: {type: GRAPHQL_TYPES.LOCAL_DATE_TIME}
};


export interface TicketFieldAggregates extends DefaultFieldAggregates {
  savings?: FieldAggregates;
}


export interface TicketPagedModel<T> extends PagedModel<T, TicketFieldAggregates> {
}


export interface TicketFetchResult<T extends Ticket> {
  ticketLinksDepth?: number;
  pagedTickets: TicketPagedModel<T[]>;
}


export interface AssigneesAggregation {
  client_count: number;
  contractor_count: number;
  customer_count: number;
}


type FieldsAndType = [Fields, TicketType];


export class TicketsApiClient extends BaseService {

  private readonly ASSIGNEES_SITE_IDS_FETCH_SIZE = 300;
  private readonly TICKET_SERVICE_PATH = '/tickets';
  private readonly TICKET_ENDPOINT_PATH = this.TICKET_SERVICE_PATH + '/tickets';

  private readonly DATA_KEY = 'tickets';

  private readonly TICKET_RELATED_DATA_QUERY_VARIABLES: VariableOptions = {
    first: { type: GRAPHQL_TYPES.INTEGER, required: true },
    offset: { type: GRAPHQL_TYPES.INTEGER, required: true },
    ticket_id: { type: GRAPHQL_TYPES.STRING, required: true }
  };

  private readonly ESCALATED_TICKET_COMMENTS_QUERY = gql.query({
    operation: this.DATA_KEY,
    fields: ['ticket_id', 'comment_count', ESCALATED_TICKET_COMMENTS_QUERY],
    variables: {
      ...this.TICKET_RELATED_DATA_QUERY_VARIABLES,
      // Needed to make GraphQL happy that the search is for escalated tickets only so can fetch comment_count
      type: { type: GRAPHQL_TYPE_TICKET_TYPE, value: TicketType.ESCALATED }
    }
  });

  private readonly ESCALATED_TICKET_ATTACHMENTS_QUERY = gql.query({
    operation: this.DATA_KEY,
    fields: ['ticket_id', { attachments: TICKET_ATTACHMENT_FIELDS }],
    variables: { ...this.TICKET_RELATED_DATA_QUERY_VARIABLES }
  });

  private readonly TICKET_CHANGE_HISTORY_QUERY_DEFINITION = {
    operation: 'change_history',
    fields: ['ticket_change_history_id', 'ticket_id', 'ts', 'system', 'author_id', 'author_full_name',
      { fields: ['field_id', 'new_value', 'old_value'] }
    ],
    variables: {
      changeHistoryStartIndex: { name: 'start_index', type: GRAPHQL_TYPES.INTEGER },
      changeHistoryLimit: { name: 'limit', type: GRAPHQL_TYPES.INTEGER },
      changeHistorySortOrder: { name: 'sort_order', type: GRAPHQL_TYPES.SORT_ORDER_ENUM }
    }
  };

  private readonly ESCALATED_TICKET_CHANGE_HISTORY_QUERY_DEFINITION = {
    ...this.TICKET_CHANGE_HISTORY_QUERY_DEFINITION,
    fields: [...this.TICKET_CHANGE_HISTORY_QUERY_DEFINITION.fields, { 'author': USER_WITH_ENTITY_FIELDS }]
  };

  private readonly ESCALATED_TICKET_CHANGE_HISTORY_QUERY = gql.query({
    operation: this.DATA_KEY,
    fields: ['ticket_id', 'change_history_count', this.ESCALATED_TICKET_CHANGE_HISTORY_QUERY_DEFINITION],
    variables: { ...this.TICKET_RELATED_DATA_QUERY_VARIABLES }
  });

  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private classTransformer: ClassTransformer,
              private siteService: SiteService,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(TicketsApiClient.name);
  }

  private getTicketsAPIEndpoint(): string {
    return this.getGateway() + this.TICKET_ENDPOINT_PATH;
  }

  private getTicketAPIEndpoint(ticketId: string): string {
    return this.getTicketsAPIEndpoint() + '/' + ticketId;
  }

  private getTicketCommentsAPIEndpoint(ticketId: string) {
    return this.getTicketAPIEndpoint(ticketId) + '/comments';
  }

  private getTicketCommentAPIEndpoint(ticketId: string, commentId: string): string {
    return this.getTicketCommentsAPIEndpoint(ticketId) + '/' + commentId;
  }

  private getTicketAttachmentsAPIEndpoint(ticketId: string): string {
    return this.getTicketAPIEndpoint(ticketId) + '/attachments';
  }

  private getTicketAttachmentAPIEndpoint(ticketId: string, attachmentId: string): string {
    return this.getTicketAttachmentsAPIEndpoint(ticketId) + '/' + attachmentId;
  }

  public getAssigneesForSites(siteIds: number[]): Observable<Map<number, User[]>> {
    if (siteIds?.length) {
      const SITE_ASSIGNEES_KEY = 'site_assignees';
      const fetchFn: ((ids: number[]) => Observable<SiteAssignees[]>) = (ids: number[]) => {
        return this.http.get(this.getGateway() + '/tickets/sites/assignees', { params: { site_ids: ids.map(id => id.toString()) } })
          .pipe(map((response: APIResponse<SiteAssignees>) => this.httpUtilityService.extractResponseData(response, SITE_ASSIGNEES_KEY)));
      };

      return this.httpUtilityService.requestPartitioned(fetchFn, siteIds, this.ASSIGNEES_SITE_IDS_FETCH_SIZE).pipe(map(response => {
        return response.reduce((result, { site_id, assignees }) => {
          const comparitorFn = UserService.userEntityAndFullNameCompare;
          // Need to turn users into objects before sorting!
          const sortedAssignees = assignees ? this.classTransformer.plainToInstance(UserService.modelMapping['User'], assignees).sort(comparitorFn) : assignees;
          return result.set(+site_id, sortedAssignees);
        }, new Map<number, User[]>());
      }));
    } else {
      return of(new Map());
    }
  }

  private handleTicketResponse<T extends Ticket>(ticketType: TicketType,
                                                 response: APIResponse<TicketAPIResultPartial<T>>,
                                                 sitesById: Map<number, SiteInfo>): TicketAPIResultPartial<T> {
    const { ticket, affected_tickets } = response?.data;
    return {
      ticket: <any>this.toTicketImpl(ticketType, ticket),
      affected_tickets: affected_tickets && affected_tickets.map(t => this.toTicketImpl(t.type, t, sitesById))
    };
  }

  public createEscalatedTicket(ticket: EscalatedTicketCreateBody): Observable<TicketAPIResultFull<EscalatedTicket>> {
    return this.http.post<APIResponse<TicketAPIResultFull<EscalatedTicketBody>>>(this.getTicketsAPIEndpoint(), ticket).pipe(
      mergeMap(response => this.getAllSiteInfos().pipe(
        map(sitesById => {
          return <TicketAPIResultFull<EscalatedTicket>>this.handleTicketResponse(TicketType.ESCALATED, response, sitesById);
        }))));
  }

  public updateEscalatedTicket(ticket: EscalatedTicket,
                               ticketUpdateBody: EscalatedTicketUpdateBody): Observable<TicketAPIResultFull<EscalatedTicket>> {
    return this.http.put<APIResponse<TicketAPIResultFull<EscalatedTicket>>>(this.getTicketAPIEndpoint(ticket.ticket_id), ticketUpdateBody).pipe(
      mergeMap(response => this.getAllSiteInfos().pipe(
        map(sitesById => <TicketAPIResultFull<EscalatedTicket>>this.handleTicketResponse(TicketType.ESCALATED, response, sitesById)))));
  }

  // here ticketPatchBody uses Partial<Ticket> rather than Partial<T> is to avoid typescript compiler error (intended typescript behaviour)
  // refer to https://github.com/microsoft/TypeScript/issues/13442 for more details
  public patchTicket<T extends Ticket>(ticket: T, ticketPatchBody: Partial<TicketBody>): Observable<TicketAPIResultPartial<T>> {
    return this.http.patch<APIResponse<TicketAPIResultPartial<T>>>(this.getTicketAPIEndpoint(ticket.ticket_id), ticketPatchBody).pipe(
      mergeMap(response => this.getAllSiteInfos().pipe(
        map(sitesById => this.handleTicketResponse(ticket.type, response, sitesById)))));
  }

  public deleteTicket(ticketId: string): Observable<void> {
    const params = new HttpParams().set('permanent', 'true');
    const options = {
      headers: this.getHeaders(),
      params
    };
    return this.http.delete<void>(this.getTicketAPIEndpoint(ticketId), options);
  }

  private getTicketCommentHttpRequestOptions(includeTicketUpdateFields?: boolean) {
    const options = { headers: this.getHeaders() };
    if (includeTicketUpdateFields) {
      options['params'] = new HttpParams().set('include_ticket_update_fields', 'true');
    }
    return options;
  }

  private handleTicketCommentResponse(response: TicketCommentAPIResponse, sitesById: Map<number, SiteInfo> | null): TicketCommentResult | null {
    const responseData = response.data;
    if (responseData) {
      const ticketData = responseData.ticket;
      const comment = responseData.ticket_comment;
      return {
        ticket_comment: this.classTransformer.plainToInstance(TicketCommentImpl, comment),
        // when editing the comment, ticket is not affected therefore the response payload does not have ticket field
        ...ticketData && { ticket: this.toEscalatedTicketImpl(ticketData, sitesById) },
      };
    }
    return null;
  }

  public createTicketComment(comment: TicketCommentCreationFields, includeTicketUpdateFields?: boolean, anonymous?: boolean): Observable<TicketCommentResult> {
    const options = this.getTicketCommentHttpRequestOptions(includeTicketUpdateFields);
    return this.http.post<TicketCommentAPIResponse>(this.getTicketCommentsAPIEndpoint(comment.ticket_id), comment, options).pipe(
      mergeMap(response => {
        const getSiteInfosObservable = anonymous ? of(null) : this.getAllSiteInfos();
        return getSiteInfosObservable.pipe(map(sitesById => this.handleTicketCommentResponse(response, sitesById)));
      }));
  }

  public updateTicketComment(comment: TicketCommentEditFields, includeTicketUpdateFields?: boolean): Observable<TicketCommentResult> {
    const options = this.getTicketCommentHttpRequestOptions(includeTicketUpdateFields);
    return this.http.put<TicketCommentAPIResponse>(this.getTicketCommentAPIEndpoint(comment.ticket_id, comment.comment_id), comment, options).pipe(
      mergeMap(response => this.getAllSiteInfos().pipe(
        map(sitesById => {
          return this.handleTicketCommentResponse(response, sitesById);
        }))));
  }

  public getAttachmentUploadUrls(fileNames: string[]): Observable<AttachmentUploadUrl[]> {
    const options = { headers: this.getHeaders() };
    return this.http.post(this.getTicketsAPIEndpoint() + '/attachments/upload-urls', { file_names: fileNames }, options)
        .pipe(map(response => response['data']['upload_urls']));
  }

  public uploadFile(url: string, file: File, progressCallback: (filename: string, progress: number) => void, completeCallback: (filename: string) => void): Observable<any> {
    // need to inline the options: https://github.com/angular/angular/issues/18424
    return this.http.put(url, file, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
        map((event: HttpEvent<any>) => {
          if (event) {
            if (event.type === HttpEventType.UploadProgress) {
              const percentDone = Math.round(100 * event.loaded / event.total);
              progressCallback(file.name, percentDone);
            } else if (event.type === HttpEventType.Response) {
              completeCallback(file.name);
            }
          }
        }),
        last()  // only return last completed event
    );
  }

  public deleteTicketAttachment(ticketId: string, attachmentId: string): Observable<any> {
    const options = { headers: this.getHeaders() };
    return this.http.delete(this.getTicketAttachmentAPIEndpoint(ticketId, attachmentId), options);
  }

  public associateAttachmentsWithTicket(ticketId: string, attachmentUrls: AttachmentUploadUrl[]): Observable<TicketAttachment[]> {
    const options = { headers: this.getHeaders() };
    const payload = {
      ticket_id: ticketId,
      attachments: attachmentUrls.map(a => ({ url: a.upload_url, file_name: a.file_name }))
    };

    return this.http.post(this.getTicketAttachmentsAPIEndpoint(ticketId), payload, options)
        .pipe(map(response => response['data']['ticket']['attachments']));
  }

  public sendEmail(email: TicketEmail, ticketId: string): Observable<any> {
    const options = { headers: this.getHeaders() };
    return this.http.post(this.getGateway() + `/tickets/email/${ticketId}`, email, options);
  }

  public getTicketAggregation(aggregationParams: TicketAggregationParams, references?: boolean):
      Observable<{ticket_aggregation: {[key: string]: any}, references?: {[key: string]: any} | null}> {
    // TODO: kq/ch create an `ObjectUtil` method to do this recurring similar code. AP-4995
    const apiReadyParams: any = {
      ...aggregationParams.type && {type: aggregationParams.type},
      ...aggregationParams.site_ids && {site_ids: aggregationParams.site_ids},
      ...aggregationParams.created_at_local_start && {created_at_local_start: aggregationParams.created_at_local_start},
      ...aggregationParams.created_at_local_end && {created_at_local_end: aggregationParams.created_at_local_end},
      ...aggregationParams.updated_at_local_start && {updated_at_local_start: aggregationParams.updated_at_local_start},
      ...aggregationParams.updated_at_local_end && {updated_at_local_end: aggregationParams.updated_at_local_end},
      ...aggregationParams.resolved_at_local_start && {resolved_at_local_start: aggregationParams.resolved_at_local_start},
      ...aggregationParams.resolved_at_local_end && {resolved_at_local_end: aggregationParams.resolved_at_local_end},
      ...aggregationParams.in_alert_local_ts_ranges && {in_alert_local_ts_ranges: aggregationParams.in_alert_local_ts_ranges},
      ...aggregationParams.ticket_archived != null && {ticket_archived: aggregationParams.ticket_archived},
      ...aggregationParams.status_ids != null && {status_ids: aggregationParams.status_ids},
      ...aggregationParams.aggregation && {aggregation: aggregationParams.aggregation},
      ...aggregationParams.age != null && {age: aggregationParams.age},
      ...aggregationParams.age_cutoff_local_ts && {age_cutoff_local_ts: aggregationParams.age_cutoff_local_ts},
      ...aggregationParams.resolved != null && {resolved: aggregationParams.resolved},
      ...aggregationParams.rule_state != null && {rule_state: aggregationParams.rule_state},
      ...aggregationParams.has_links != null && {has_links: aggregationParams.has_links},
      ...aggregationParams.linked_ticket_recorded_at_local_end && {linked_ticket_recorded_at_local_end: aggregationParams.linked_ticket_recorded_at_local_end},
      ...aggregationParams.time_in_fault != null && {time_in_fault: aggregationParams.time_in_fault},
      ...aggregationParams.time_in_fault_boundaries != null && {time_in_fault_boundaries: aggregationParams.time_in_fault_boundaries},
      ...aggregationParams.age_boundaries != null && {age_boundaries: aggregationParams.age_boundaries},
      ...references != null && {references: references},
    };
    const options = {headers: this.getHeaders(), params: apiReadyParams};
    // return ['data'] which contains ['ticket_aggregation'] and optional ['references']
    return this.http.get(this.getTicketsAPIEndpoint() + '/aggregation', options).pipe(map(response => {
      const data: {ticket_aggregation: {[key: string]: any}, references?: {[key: string]: any} | null} = response[this.DATA_PREFIX];
      if (data?.references?.users) {
        data.references.users = this.classTransformer.plainToInstance(UserService.modelMapping['User'], data.references.users);
      }
      return data;
    }));
  }

  public getBulkTicketAggregation(params: BulkTicketAggregationParams): Observable<{ticket_aggregations: {[key: string]: any}[]}> {
    const apiReadyParams: Omit<BulkTicketAggregationParams, 'bulk_search_criteria'> & {bulk_search_criteria: string[]} = {
      ...params.aggregation && {aggregation: params.aggregation},
      ...params.references != null && {references: params.references},
      ...params.time_in_fault != null && {time_in_fault: params.time_in_fault},
      ...params.age != null && {age: params.age},
      bulk_search_criteria: params.bulk_search_criteria.map(searchCriteria => JSON.stringify(searchCriteria)),
    };
    const options = {headers: this.getHeaders(), params: apiReadyParams};
    return this.http.get(this.getTicketsAPIEndpoint() + '/aggregation/bulk', options).pipe(map(response => {
      return response[this.DATA_PREFIX];
    }));
  }

  private graphqlSearchTickets(operationName: string,
                               fields: Fields,
                               graphqlVariables: VariableOptions,
                               graphqlSearchParams: { [key: string]: any }): Observable<TicketPagedModel<Ticket[]>> {
    const ticketFields = fields.some(f => f === TICKET_FIELD_TYPE) ? fields : [...fields, TICKET_FIELD_TYPE];
    const query = gql.query({
      operation: operationName,
      fields: ticketFields,
      variables: graphqlVariables,
    });

    return this.httpUtilityService.ticketsGraphQL<Ticket[]>(query, operationName, graphqlSearchParams).pipe(
      mergeMap((result: PagedModel<Ticket[]>) => {
        return this.getAllSiteInfos().pipe(map(sitesById => ({
          ...result,
          data: result.data.map(ticketData => this.toTicketImpl(ticketData.type, ticketData, sitesById)),
        })));
      }));
  }

  public getCommentsForEscalatedTicket(ticketId: string, offset: number, limit: number): Observable<{ count: number, comments: TicketComment[] }> {
    const searchParams = {
      ticket_id: ticketId,
      first: 1,
      offset: 0,
      commentsFirst: limit,
      commentsOffset: offset,
    };

    return this.httpUtilityService.ticketsGraphQL<EscalatedTicket[]>(
      this.ESCALATED_TICKET_COMMENTS_QUERY,
      this.DATA_KEY,
      searchParams
    ).pipe(map(result => {
      const ticket = this.toEscalatedTicketImpl(CollectionUtils.only(result.data));
      return ticket && { count: ticket.comment_count, comments: ticket.comments };
    }));
  }

  public getAttachmentsForTicket(ticketId: string): Observable<TicketAttachment[]> {
    const searchParams = {
      ticket_id: ticketId,
      first: 1,
      offset: 0
    };

    return this.httpUtilityService.ticketsGraphQL<Ticket[]>(
      this.ESCALATED_TICKET_ATTACHMENTS_QUERY,
      this.DATA_KEY,
      searchParams
    ).pipe(map(result => {
      const ticket = this.toEscalatedTicketImpl(CollectionUtils.only(result.data));
      return ticket && ticket.attachments;
    }));
  }

  public getChangeHistoryForEscalatedTicket(ticketId: string,
                                            start_index: number,
                                            limit: number,
                                            sort_order?: SortOrder): Observable<{ count: number, change_history: TicketChangeHistory[] }> {
    const searchParams = {
      changeHistoryStartIndex: start_index ?? 0,
      changeHistoryLimit: limit ?? 1,
      ...sort_order && { changeHistorySortOrder: sort_order },
      ticket_id: ticketId,
      first: 1,
      offset: 0
    };

    return this.httpUtilityService.ticketsGraphQL<EscalatedTicket[]>(
      this.ESCALATED_TICKET_CHANGE_HISTORY_QUERY,
      this.DATA_KEY,
      searchParams
    ).pipe(map(result => {
      const ticket = this.toEscalatedTicketImpl(CollectionUtils.only(result.data));
      return ticket && { count: ticket.change_history_count, change_history: ticket.change_history };
    }));
  }

  private getAllSiteInfos(): Observable<Map<number, SiteInfo>> {
    return this.siteService.getCachedSiteInfosById();
  }

  private toEscalatedTicketImpl(ticketData: PartialTicket, sitesById?: Map<number, SiteInfo> | null): EscalatedTicketImpl {
    const escalatedTicket = this.classTransformer.plainToInstance(EscalatedTicketImpl, ticketData);
    this.addSiteInfoToTicket(escalatedTicket, sitesById);
    return escalatedTicket;
  }

  private toAlertTicketImpl(ticketData: PartialTicket, sitesById?: Map<number, SiteInfo> | null): AlertTicketImpl {
    const alertTicket = this.classTransformer.plainToInstance(AlertTicketImpl, ticketData);
    this.addSiteInfoToTicket(alertTicket, sitesById);
    return alertTicket;
  }

  private toTicketImpl(ticketType: TicketType, ticket: PartialTicket, sitesById?: Map<number, SiteInfo> | null): AlertTicketImpl | EscalatedTicketImpl {
    if (ticketType == null) {
      throw new Error('Cannot transform ticket object as it does not contain ticket type field.');
    }
    let ticketObject;
    switch (ticketType) {
      case TicketType.ALERT:
        ticketObject = this.toAlertTicketImpl(ticket, sitesById);
        break;
      case TicketType.ESCALATED:
        ticketObject = this.toEscalatedTicketImpl(ticket, sitesById);
        break;
      default:
        throw new Error('Invalid ticket type ' + ticketType);
    }
    return ticketObject;
  }

  private addSiteInfoToTicket(ticket: Ticket, sitesById: Map<number, SiteInfo> | null): void {
    if (ticket.site == null && ticket.site_id != null) {
      ticket.site = sitesById?.get(ticket.site_id);
    }
  }

  public getTicketsById(ticketIds: string[], limit: number, offset: number): Observable<(AlertTicket | EscalatedTicket)[]> {
    const searchParams = {
      ticket_ids: ticketIds,
      // todo ch: remove the following 2 params as they are not needed and cause devmode exceptions
      commentsFirst: 1,
      commentsOffset: 0,
      orderBy: 'savings DESC, priority_id DESC',
      first: limit,
      offset,
    };
    return this.graphqlSearchTickets(this.DATA_KEY, BASE_TICKET_FIELDS, BASE_TICKETS_SEARCH_VARIABLES, searchParams)
        .pipe(map((pagedTickets: TicketPagedModel<Ticket[]>) => pagedTickets.data));
  }

  private getTickets<T extends Ticket>(operationName: string,
                                       fields: Fields,
                                       graphqlVariables: VariableOptions,
                                       graphqlSearchParams: { [key: string]: any },
                                       ticketFetchingDepth: number): Observable<TicketFetchResult<T>> {
    return this.graphqlSearchTickets(operationName, fields, graphqlVariables, graphqlSearchParams).pipe(
      map((pagedTickets: TicketPagedModel<Ticket[]>) => ({
        ticketLinksDepth: ticketFetchingDepth,
        pagedTickets: <TicketPagedModel<T[]>>pagedTickets
      })));
  }

  private getTicketLinksFields([[linkedTicketFields, linkedTicketType], ...nestedLinkedTicketFieldsRest]: FieldsAndType[]): Fields {
    const ticketFields = nestedLinkedTicketFieldsRest?.length
        ? [...linkedTicketFields, {ticket_links: this.getTicketLinksFields(nestedLinkedTicketFieldsRest)}]
        : linkedTicketFields;
    // The graphql query parameter name. Needs to be unique so that the nested query parameter `type` can be given a different value to the root `tickets` parameter `type`.
    const typeVariableName = 'linkedTicketType_' + (nestedLinkedTicketFieldsRest?.length ?? 0);
    return [
      { ticket_link_type: ['name'] },
      'direction',
      {
        operation: 'ticket',
        fields: ticketFields,
        variables: {
          [typeVariableName]: {name: 'type', type: GRAPHQL_TYPE_TICKET_TYPE, value: linkedTicketType}
        }
      }
    ];
  }

  private isFetchingCommentsForTicket(ticketType: TicketType, fields: Fields): boolean {
    return ticketType === TicketType.ESCALATED
      && fields.some(field => isNestedField(field)
        ? field.operation === TICKET_COMMENTS_QUERY.operation
        : (typeof field === 'object' && Array.isArray(field[<string>TICKET_COMMENTS_QUERY.operation])));
  }

  private getSearchTicketsVariables(ticketType: TicketType, fields: Fields, searchParams: TicketSearchParams, limit: number, offset: number, orderBy?: string[]) {
    return {
      ticket_archived: false,
      first: limit,
      offset,
      ...this.isFetchingCommentsForTicket(ticketType, fields) && {
        commentsFirst: TicketsConstants.COMMENT_FETCH_SIZE,
        commentsOffset: 0
      },
      ...orderBy?.length && { orderBy: orderBy.join(',') },
      ...searchParams,
      ...searchParams.in_alert_local_ts_range && {
        in_alert_local_ts_range: MomentUtil.rangeToLocalInterval(searchParams.in_alert_local_ts_range),
      },
      type: ticketType
    };
  }

  public getEscalatedTicketsWithFields(fields: Fields,
                                       searchParams: EscalatedTicketSearchParams,
                                       limit: number,
                                       offset: number,
                                       orderBy?: string[],
                                       ticketFetchingDepth: number = 1): Observable<TicketFetchResult<EscalatedTicket>> {
    const variableValues = {
      ...this.getSearchTicketsVariables(TicketType.ESCALATED, fields, searchParams, limit, offset, orderBy),
      ...searchParams.totals?.length && { totals: searchParams.totals },
      ...searchParams.has_savings != null && { has_savings: searchParams.has_savings },
    };
    return this.getTickets(this.DATA_KEY, fields, ESCALATED_TICKETS_SEARCH_VARIABLES, variableValues, ticketFetchingDepth);
  }

  public getEscalatedTickets(searchParams: EscalatedTicketSearchParams,
                             limit: number,
                             offset: number,
                             orderBy?: string[],
                             fetchOptions?: EscalatedTicketFetchOptions): Observable<TicketFetchResult<EscalatedTicket>> {
    const ticketLinksFieldsLevels: FieldsAndType[] = [[ALERT_TICKET_FIELDS, TicketType.ALERT], [['ticket_id'], TicketType.ESCALATED]];
    const fields = [
      ...ESCALATED_TICKET_FIELDS,
      ...fetchOptions?.includeTicketAge ? [TICKET_AGE_FIELD] : [],
      ...fetchOptions?.includeLinkedTickets
          ? [{ ticket_links: this.getTicketLinksFields(ticketLinksFieldsLevels) }]
          : [],
    ];

    return this.getEscalatedTicketsWithFields(fields, searchParams, limit, offset, orderBy, ticketLinksFieldsLevels.length);
  }

  public getAlertTickets(searchParams: AlertTicketSearchParams,
                         limit: number,
                         offset: number,
                         orderBy: string[],
                         fetchOptions?: AlertTicketFetchOptions): Observable<TicketFetchResult<AlertTicket>> {
    const ticketLinksFieldsLevels: FieldsAndType[] = [
      [ESCALATED_TICKET_FIELDS, TicketType.ESCALATED],
      [ALERT_TICKET_FIELDS, TicketType.ALERT],
      [['ticket_id'], TicketType.ESCALATED]
    ];
    const alertTicketFields = [
      ...ALERT_TICKET_FIELDS,
      ...fetchOptions?.includeTimeInFault ? [TIME_IN_FAULT_FIELD] : [],
      ...fetchOptions?.includeLinkedTickets
          ? [{ ticket_links: this.getTicketLinksFields(ticketLinksFieldsLevels) }]
          : [],
    ];
    const variableValues = this.getSearchTicketsVariables(TicketType.ALERT, alertTicketFields, searchParams, limit, offset, orderBy);
    return this.getTickets(this.DATA_KEY, alertTicketFields, ALERT_TICKETS_SEARCH_VARIABLES, variableValues, ticketLinksFieldsLevels.length);
  }

  public getAssigneesAggregation(siteIds: number[]): Observable<AssigneesAggregation> {
    this.logger.debug('getAssigneesAggregation', siteIds);
    const params: any = {
      aggregate_entity: 'all',
      site_ids: siteIds
    };
    const options = {headers: this.getHeaders(), params: params};
    return this.http.get(this.getGateway() + '/sites/assignees/aggregation', options).pipe(map(response => {
      const aggregation = response[this.DATA_PREFIX]?.['assignees_aggregation'];
      return aggregation?.length ? aggregation[0] : null;
    }));
  }


}
